import React, { useState, useRef } from "react";
import { Plus, Trash2, GripVertical } from "lucide-react";

export default function KeyContactsSection({ form, setForm, crew, Section }) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const inputRef = useRef(null);

  const delKC = (i) => setForm(f => ({ ...f, key_contacts: (f.key_contacts || []).filter((_, idx) => idx !== i) }));
  const moveKC = (from, to) => setForm(f => {
    const list = f.key_contacts || [];
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return f;
    const next = [...list];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    return { ...f, key_contacts: next.map((c, idx) => ({ ...c, sort_order: idx })) };
  });
  const onDragStart = (i) => (e) => { setDragIdx(i); e.dataTransfer.effectAllowed = "move"; };
  const onDragOver = (i) => (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setOverIdx(i); };
  const onDrop = (i) => (e) => { e.preventDefault(); if (dragIdx !== null) moveKC(dragIdx, i); setDragIdx(null); setOverIdx(null); };
  const onDragEnd = () => { setDragIdx(null); setOverIdx(null); };

  const matches = (() => {
    const q = query.trim().toLowerCase();
    const added = new Set((form.key_contacts || []).map(c => c.crew_id));
    if (!q) return [];
    return crew.filter(m => !added.has(m.id))
      .filter(m => {
        const hay = `${m.name || ""} ${m.role || m.position || ""} ${m.department || ""}`.toLowerCase();
        return hay.includes(q);
      })
      .slice(0, 8);
  })();

  const pick = (m) => {
    if ((form.key_contacts || []).some(c => c.crew_id === m.id)) return;
    setForm(f => ({
      ...f,
      key_contacts: [...(f.key_contacts || []), {
        crew_id: m.id, name: m.name || "", role: m.role || m.position || "",
        department: m.department || "", phone: m.phone || "", email: m.email || "",
        sort_order: (f.key_contacts || []).length,
      }],
    }));
    setQuery("");
    inputRef.current?.focus();
  };

  return (
    <Section title="Key Contacts">
      <div className="pt-4 space-y-3" data-testid="key-contacts-section">
        <div className="relative">
          <input ref={inputRef} data-testid="key-contacts-search-input"
            value={query} onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onKeyDown={(e) => { if (e.key === "Enter" && matches.length > 0) { e.preventDefault(); pick(matches[0]); } }}
            placeholder={crew.length === 0 ? "Add crew to your Crew Database first…" : "Start typing a crew name, position, or department…"}
            disabled={crew.length === 0}
            className="w-full bg-[#0D0F12] border border-[#28303F] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-sm text-white placeholder:text-gray-600 transition-colors" />
          {focused && query.trim() && (
            <div data-testid="key-contacts-suggestions"
              className="absolute z-30 left-0 right-0 mt-1 bg-[#14181F] border border-[#28303F] rounded-lg shadow-2xl max-h-72 overflow-y-auto">
              {matches.length === 0 ? (
                <p className="text-xs text-gray-600 px-3 py-3">No matches. Try a different name or add them in the Crew Database.</p>
              ) : (
                matches.map(m => (
                  <button key={m.id} type="button" data-testid="key-contacts-suggestion-item"
                    onMouseDown={(e) => { e.preventDefault(); pick(m); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-[#1A1F29] text-left transition-colors">
                    <div>
                      <p className="text-sm text-white font-medium">{m.name}</p>
                      <p className="text-xs text-gray-500">
                        <span className="text-amber-400/80">{m.role || m.position || "—"}</span>
                        {m.department && <span className="text-gray-600"> · {m.department}</span>}
                      </p>
                    </div>
                    <Plus size={14} className="text-amber-400 shrink-0"/>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
        {(form.key_contacts || []).length === 0 ? (
          <p className="text-xs text-gray-600 text-center py-3">No key contacts yet. Search above to pick from the crew database — position auto-fills.</p>
        ) : (
          <>
            <p className="text-xs text-gray-500 flex items-center gap-1">Drag <GripVertical size={11} className="inline text-amber-400/70"/> to reorder.</p>
            <div className="space-y-1.5">
              {(form.key_contacts || []).map((kc, i) => (
                <div key={kc.crew_id + i} data-testid="key-contact-row" draggable
                  onDragStart={onDragStart(i)} onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)} onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 bg-[#0D0F12] border rounded-lg px-3 py-2.5 transition-all ${overIdx === i ? "border-amber-500/60" : "border-[#28303F]"} ${dragIdx === i ? "opacity-50" : ""}`}>
                  <GripVertical size={14} className="text-gray-500 cursor-grab shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white font-medium truncate" data-testid="key-contact-name">{kc.name || "Unnamed"}</p>
                    <p className="text-xs text-gray-500 truncate">
                      <span className="text-amber-400/80" data-testid="key-contact-role">{kc.role || "—"}</span>
                      {kc.department && <span className="text-gray-600"> · {kc.department}</span>}
                      {kc.phone && <span className="text-gray-600"> · {kc.phone}</span>}
                    </p>
                  </div>
                  <button type="button" onClick={() => delKC(i)}
                    className="text-gray-500 hover:text-red-400 p-1" data-testid="key-contact-delete-btn">
                    <Trash2 size={14}/>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </Section>
  );
}
